import { STATUS } from "@/constants/statuses";
import ReviewsListResponseEntity from "@apps/reviews/entities/ReviewsLIstResponseEntity";
import ReviewsListResponseSchema from "@apps/reviews/schemas/ReviewsListResponseSchema";
import ReviewsStoreBase from "@apps/reviews/stores/ReviewsStoreBase";
import { applyTransaction } from "@datorama/akita";
import { ApiClientQueryParams, ApiClientResponse } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create } from "superstruct";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ReviewEntity from "../entities/ReviewEntity";
import ReviewsSortingOptionsEntity from "../entities/ReviewsSortingOptionsEntity";
import VotesStore from "../stores/VotesStore";

export type GetReviewsParamsDTO = {
    productId?: number;
    collectionIds?: string;
    offset?: number;
    sortingOptions?: ReviewsSortingOptionsEntity[] | string;
    search?: string;
    onlyShopReviews?: boolean;
    onlyProductReviews?: boolean;
    onlyPinnedReviews?: boolean;
    onlyWithPhotos?: boolean;
    lowestRating?: number;
    perPage?: number;
    reviewIds?: number[];
};

export type GetReviewsWithFiltersParamsDTO = GetReviewsParamsDTO &
    ApiClientQueryParams;

export type GetReviewsRequestParamsDTO = GetReviewsParamsDTO & {
    customAttributeIds?: string | number;
};

class ReviewsServiceBase {
    constructor(
        protected readonly reviewsStore: ReviewsStoreBase,
        protected readonly votesStore: VotesStore,
        protected readonly authApiClient: ApiClientInterface,
        protected readonly logger: LoggerInterface,
        protected readonly productsService?: ProductsServiceInterface
    ) {}

    protected getReviewsRequest(
        reqParams?: GetReviewsWithFiltersParamsDTO
    ): Observable<ApiClientResponse<unknown>> {
        this.logger.debug(
            "ReviewsService.getReviewsRequest is started",
            reqParams
        );

        return this.authApiClient.get({
            url: "/review/getReviewList",
            responseType: "json",
            queryParams: reqParams,
        });
    }

    protected addProductsForFetching(reviews: ReviewEntity[]) {
        const productsService = this.productsService;

        if (productsService) {
            reviews.forEach((review) => {
                if (review.product) {
                    productsService.addProductForFetching(review.product.id);
                }
            });
        }
    }

    getReviews(
        reqParams?: GetReviewsWithFiltersParamsDTO
    ): Observable<ReviewsListResponseEntity> {
        this.reviewsStore.setStatus(STATUS.INITIAL_LOADING);

        return this.getReviewsRequest(reqParams).pipe(
            map((v) => create(v.body, ReviewsListResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is errored",
                    { reqParams, error }
                );
                applyTransaction(() => {
                    this.reviewsStore.setError(error);
                    this.reviewsStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((v) => {
                this.addProductsForFetching(v.items);
            }),
            tap((v) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is ready",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsStore.set(v.items);
                    this.votesStore.add(
                        v.items.map((item) => ({
                            reviewId: item.id,
                            votes: item.votes,
                            isCustomerVoted: item.isCustomerVoted,
                            status: STATUS.VIRGIN,
                        }))
                    );
                    this.reviewsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));
                    this.reviewsStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    loadMoreReviews(
        reqParams: GetReviewsWithFiltersParamsDTO
    ): Observable<ReviewsListResponseEntity> {
        this.reviewsStore.setStatus(STATUS.LOADING);

        return this.getReviewsRequest(reqParams).pipe(
            map((v) => create(v.body, ReviewsListResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is errored",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsStore.setError(error);
                    this.reviewsStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((v) => {
                this.addProductsForFetching(v.items);
            }),
            tap((v) => {
                this.logger.debug(
                    "ReviewsService.getReviewsRequest is ready",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsStore.add(v.items);
                    this.votesStore.add(
                        v.items.map((item) => ({
                            reviewId: item.id,
                            votes: item.votes,
                            isCustomerVoted: item.isCustomerVoted,
                            status: STATUS.VIRGIN,
                        }))
                    );
                    this.reviewsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));

                    this.reviewsStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    appendReview(review: ReviewEntity) {
        applyTransaction(() => {
            this.reviewsStore.add(review, { prepend: true });
            this.reviewsStore.update((state) => ({
                ...state,
                paginationMeta: {
                    ...state.paginationMeta,
                    totalCount: state.paginationMeta.totalCount + 1,
                },
            }));
        });
    }

    updateReview(review: ReviewEntity) {
        applyTransaction(() => {
            const state = this.reviewsStore.getValue();
            if (!state.entities) return;
            const existingReview = Object.values(state.entities).find(
                (entity) => entity.id === review.id
            );
            if (existingReview) {
                this.reviewsStore.remove(existingReview.uid);
            }
            this.reviewsStore.add(review, { prepend: true });
        });
    }
}

export default ReviewsServiceBase;
